import { useState } from 'react';
import type { ApiClient, Elektroverteiler, Elektrostromkreis } from '../api';
import { Feld as MusterFeld, FormSection, leseZahl } from '../muster';

/**
 * Die Nachweisdaten pflegen — je Verteiler und je Abgang.
 *
 * --- Warum eine eigene Maske ------------------------------------------------------
 *
 * Der Vorschlagslauf füllt die Felder mit üblichen Ansätzen. Wer sie berichtigt oder
 * einen Wert erstmals einträgt, braucht ein Feld — und der Datensatz gilt danach als
 * `gepflegt` statt `vorschlag`. Erst dann meldet das Qualitätsgate ihn nicht mehr als
 * ungeprüft.
 *
 * --- Nur Geändertes wird geschickt ------------------------------------------------
 *
 * Ein nicht angefasstes Feld geht nicht mit. Sonst wäre jedes Speichern ein
 * Löschbefehl für alles, was die Maske nicht anzeigt — und bei 45 Feldern zeigt sie
 * nie alles.
 */

type Werte = Record<string, string>;

/**
 * Ein Eingabefeld der Nachweismaske.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * UMZUG AUF `Feld` DER MUSTERBIBLIOTHEK (Block U, Paket U6b)
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Vorher stand die Einheit IN der Beschriftung (`Bemessungsspannung [V]`).
 * Nach Abschnitt 15 gehören Wert und Einheit getrennt: `art: 'zahl'` reicht
 * an `WertEinheitFeld` durch, und dort steht die Einheit in einem eigenen
 * Element neben dem Feld.
 *
 * Der Zustand bleibt eine Zeichenkette, weil die Änderungserkennung und der
 * Aufbau der Nutzlast darauf stehen — ein leeres Feld ist ein Löschbefehl
 * und darf nicht zu 0 werden. `leseZahl`/`String` bilden nur an der Grenze
 * um.
 */
function Feld({
  name, bez, einheit, hinweis, auswahl, zahl, werte, setzen,
}: {
  name: string;
  bez: string;
  einheit?: string;
  hinweis?: string;
  auswahl?: string[];
  /** Zahlenfeld - bekommt die Einheit neben das Feld statt in die Beschriftung. */
  zahl?: boolean;
  werte: Werte;
  setzen: (n: string, w: string) => void;
}) {
  const wert = werte[name] ?? '';

  if (auswahl) {
    return (
      <MusterFeld
        art="auswahl"
        beschriftung={bez}
        wert={wert}
        beiAenderung={(w) => setzen(name, w as string)}
        hinweis={hinweis}
        optionen={[
          { wert: '', beschriftung: '—' },
          ...auswahl.map((a) => ({ wert: a, beschriftung: a })),
          // Ein gepflegter Wert außerhalb der Liste bleibt wählbar - sonst
          // löschte ihn das erste Öffnen der Maske.
          ...(wert && !auswahl.includes(wert)
            ? [{ wert, beschriftung: wert }] : []),
        ]}
        breite="drittel"
      />
    );
  }

  if (zahl) {
    return (
      <MusterFeld
        art="zahl"
        beschriftung={bez}
        einheit={einheit}
        wert={wert === '' ? null : leseZahl(wert)}
        beiAenderung={(w) => setzen(name, w === null ? '' : String(w))}
        hinweis={hinweis}
        breite="drittel"
      />
    );
  }

  return (
    <MusterFeld
      beschriftung={bez}
      wert={wert}
      beiAenderung={(w) => setzen(name, w as string)}
      hinweis={hinweis}
      breite="drittel"
    />
  );
}

// === Der Verteiler ===========================================================

export function VerteilerNachweisdaten({
  api, verteiler, fertig,
}: {
  api: ApiClient;
  verteiler: Elektroverteiler;
  fertig: (m: string) => void;
}) {
  const [werte, setWerte] = useState<Werte>(() => ({
    netzform: verteiler.netzform ?? '',
    bemessungsspannung: verteiler.bemessungsspannung?.toString() ?? '',
    frequenz: '',
    phasenanzahl: '',
    bemessungsstrom: verteiler.bemessungsstrom?.toString() ?? '',
    kurzschlussstrom: verteiler.kurzschlussstrom?.toString() ?? '',
    kurzschlussstromMin: '',
    vorgelagertesSchutzorgan: '',
    neutralleiterKonzept: '',
    ueberspannungsschutz: '',
    schranksystem: '',
    schrankHersteller: '',
    schrankBaureihe: '',
    aufstellungsort: verteiler.raumnummer ?? '',
    umgebungstemperaturMax: '',
    sammelschieneStrom: '',
    sammelschieneKurzschlussfest: '',
    verlustleistungZulaessig: '',
    norm61439Teil: '',
    teJeFeld: verteiler.teJeFeld?.toString() ?? '',
    felder: verteiler.felder?.toString() ?? '',
  }));

  const [urzustand] = useState<Werte>({ ...werte });
  const [laeuft, setLaeuft] = useState(false);

  const setzen = (n: string, w: string) => setWerte({ ...werte, [n]: w });

  const geaendert = Object.keys(werte).some((n) => werte[n] !== urzustand[n]);

  const speichern = async () => {
    setLaeuft(true);
    try {
      // Nur das Geänderte. Zahlen als Zahl, Text als Text, leer als null.
      const nutzlast: Record<string, string | number | null> = {};

      const zahlen = new Set([
        'bemessungsspannung', 'frequenz', 'phasenanzahl', 'bemessungsstrom',
        'kurzschlussstrom', 'kurzschlussstromMin', 'umgebungstemperaturMax',
        'sammelschieneStrom', 'sammelschieneKurzschlussfest',
        'verlustleistungZulaessig', 'teJeFeld', 'felder',
      ]);

      for (const n of Object.keys(werte)) {
        if (werte[n] === urzustand[n]) continue;

        const roh = werte[n].trim();

        if (roh === '') { nutzlast[n] = null; continue; }

        nutzlast[n] = zahlen.has(n)
          ? Number(roh.replace(',', '.'))
          : roh;
      }

      const a = await api.elektroVerteilerNachweis(verteiler.id, nutzlast);

      fertig(a ? a.message : 'Gespeichert.');
    } catch (e) {
      fertig(e instanceof Error ? e.message : String(e));
    } finally {
      setLaeuft(false);
    }
  };

  return (
    <div>
      <FormSection titel="Netz und Einspeisung" spalten={3}>
        <Feld name="netzform" bez="Netzform" werte={werte} setzen={setzen}
              auswahl={['TN-S', 'TN-C', 'TN-C-S', 'TT', 'IT']}
              hinweis="Im TN-System gilt eine andere Abschaltbedingung als im TT-System." />

        <Feld name="bemessungsspannung" bez="Bemessungsspannung" einheit="V"
              zahl werte={werte} setzen={setzen} />

        <Feld name="frequenz" bez="Frequenz" einheit="Hz"
              zahl werte={werte} setzen={setzen} />

        <Feld name="phasenanzahl" bez="Phasenanzahl"
              werte={werte} setzen={setzen} auswahl={['1', '3']} />

        <Feld name="bemessungsstrom" bez="Bemessungsstrom" einheit="A"
              zahl werte={werte} setzen={setzen} />

        <Feld name="kurzschlussstrom" bez="Ik'' max" einheit="kA"
              zahl werte={werte} setzen={setzen}
              hinweis="Für das Ausschaltvermögen der Schutzeinrichtungen." />

        <Feld name="kurzschlussstromMin" bez="Ik'' MIN" einheit="kA"
              zahl werte={werte} setzen={setzen}
              hinweis="Der maßgebende für die Abschaltbedingung. Mit dem maximalen gerechnet besteht sie immer." />

        <Feld name="vorgelagertesSchutzorgan" bez="Vorgelagerte Sicherung"
              werte={werte} setzen={setzen} />

        <Feld name="neutralleiterKonzept" bez="N-/PE-Konzept"
              werte={werte} setzen={setzen} />

        <Feld name="ueberspannungsschutz" bez="Überspannungsschutz"
              werte={werte} setzen={setzen}
              auswahl={['Typ 1', 'Typ 1+2', 'Typ 2', 'Typ 3', 'ohne']} />
      </FormSection>

      <FormSection titel="Schrank und Aufstellung" spalten={3}>
        <Feld name="schranksystem" bez="Schranksystem"
              werte={werte} setzen={setzen} />
        <Feld name="schrankHersteller" bez="Hersteller"
              werte={werte} setzen={setzen} />
        <Feld name="schrankBaureihe" bez="Baureihe"
              werte={werte} setzen={setzen} />
        <Feld name="aufstellungsort" bez="Aufstellungsort"
              werte={werte} setzen={setzen} />

        <Feld name="umgebungstemperaturMax" bez="Umgebungstemperatur" einheit="°C"
              zahl werte={werte} setzen={setzen}
              hinweis="Im Schrank, nicht im Raum: Raumtemperatur plus Eigenerwärmung." />

        <Feld name="teJeFeld" bez="TE je Feld" werte={werte} setzen={setzen}
              auswahl={['24', '36', '48']} />

        <Feld name="felder" bez="Felder vorhanden"
              zahl werte={werte} setzen={setzen} />

        <Feld name="norm61439Teil" bez="DIN EN IEC 61439 Teil"
              werte={werte} setzen={setzen} auswahl={['-2', '-3']}
              hinweis="-2 für Schaltgerätekombinationen, -3 für Installationsverteiler." />
      </FormSection>

      <FormSection
        titel="Sammelschiene"
        spalten={3}
        fuss={(
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <button onClick={speichern} disabled={!geaendert || laeuft}
                    className="m-knopf m-knopf-haupt">
              {laeuft ? 'wird gespeichert …' : 'Nachweisdaten speichern'}
            </button>

            <span className="sehr-schwach" style={{ fontSize: 11 }}>
              Danach gilt der Verteiler als <em>gepflegt</em> — das Qualitätsgate
              meldet ihn nicht mehr als Vorschlag.
            </span>
          </div>
        )}
      >
        <Feld name="sammelschieneStrom" bez="Bemessungsstrom" einheit="A"
              zahl werte={werte} setzen={setzen} />
        <Feld name="sammelschieneKurzschlussfest" bez="Icw" einheit="kA"
              zahl werte={werte} setzen={setzen} />
        <Feld name="verlustleistungZulaessig" bez="Zul. Verlustleistung" einheit="W"
              zahl werte={werte} setzen={setzen} />
      </FormSection>
    </div>
  );
}

// === Ein Abgang ==============================================================

export function KreisNachweisdaten({
  api, kreis, fertig, geschlossen,
}: {
  api: ApiClient;
  kreis: Elektrostromkreis;
  fertig: (m: string) => void;
  geschlossen: () => void;
}) {
  const [werte, setWerte] = useState<Werte>(() => ({
    betriebsstrom: kreis.betriebsstrom?.toString() ?? '',
    leistungsfaktor: kreis.leistungsfaktor?.toString() ?? '',
    gleichzeitigkeit: kreis.gleichzeitigkeit?.toString() ?? '',
    phase: kreis.phase ?? '',
    querschnitt: kreis.querschnitt?.toString() ?? '',
    leiterwerkstoff: kreis.leiterwerkstoff ?? '',
    belasteteAdern: kreis.belasteteAdern?.toString() ?? '',
    verlegeart: kreis.verlegeart ?? '',
    haeufung: kreis.haeufung?.toString() ?? '',
    umgebungstemperaturC: kreis.umgebungstemperaturC?.toString() ?? '',
    laenge: kreis.laenge?.toString() ?? '',
    leitungsart: kreis.leitungsart ?? '',
    cprKlasse: kreis.cprKlasse ?? '',
    schutzorgan: kreis.schutzorgan ?? '',
    schutzorganHersteller: kreis.schutzorganHersteller ?? '',
    schutzorganTyp: kreis.schutzorganTyp ?? '',
    kennlinie: kreis.kennlinie ?? '',
    bemessungsstrom: kreis.bemessungsstrom?.toString() ?? '',
    ausschaltvermoegen: kreis.ausschaltvermoegen?.toString() ?? '',
    rcdTyp: kreis.rcdTyp ?? '',
    rcdBemessungsstrom: kreis.rcdBemessungsstrom?.toString() ?? '',
    rcdDifferenzstrom: kreis.rcdDifferenzstrom?.toString() ?? '',
    rcdPolzahl: kreis.rcdPolzahl?.toString() ?? '',
    zusatzgeraet: kreis.zusatzgeraet ?? '',
    klemmeN: kreis.klemmeN ?? '',
    klemmePe: kreis.klemmePe ?? '',
  }));

  const [urzustand] = useState<Werte>({ ...werte });
  const [laeuft, setLaeuft] = useState(false);

  const setzen = (n: string, w: string) => setWerte({ ...werte, [n]: w });

  const geaendert = Object.keys(werte).some((n) => werte[n] !== urzustand[n]);

  const zahlen = new Set([
    'betriebsstrom', 'leistungsfaktor', 'gleichzeitigkeit', 'querschnitt',
    'belasteteAdern', 'haeufung', 'umgebungstemperaturC', 'laenge',
    'bemessungsstrom', 'ausschaltvermoegen', 'rcdBemessungsstrom',
    'rcdDifferenzstrom', 'rcdPolzahl',
  ]);

  const speichern = async () => {
    setLaeuft(true);
    try {
      const nutzlast: Record<string, string | number | null> = {};

      for (const n of Object.keys(werte)) {
        if (werte[n] === urzustand[n]) continue;

        const roh = werte[n].trim();

        if (roh === '') { nutzlast[n] = null; continue; }

        nutzlast[n] = zahlen.has(n) ? Number(roh.replace(',', '.')) : roh;
      }

      const a = await api.elektroKreisNachweis(kreis.id, nutzlast);

      fertig(a ? a.message : 'Gespeichert.');
      geschlossen();
    } catch (e) {
      fertig(e instanceof Error ? e.message : String(e));
    } finally {
      setLaeuft(false);
    }
  };

  return (
    <div style={{ padding: '8px 0' }}>
      <FormSection titel="Last und Leitung" spalten={3}>
        <Feld name="betriebsstrom" bez="Betriebsstrom Ib" einheit="A"
              zahl werte={werte} setzen={setzen}
              hinweis="Leer lassen — dann wird er aus Leistung, cos φ und Gleichzeitigkeit gerechnet." />

        <Feld name="leistungsfaktor" bez="cos φ" zahl werte={werte} setzen={setzen}
              hinweis="Bei LED-Treibern ohne Korrektur 0,5 bis 0,7 — dann verdoppelt sich der Strom fast." />

        <Feld name="gleichzeitigkeit" bez="Gleichzeitigkeit"
              zahl werte={werte} setzen={setzen} />

        <Feld name="phase" bez="Phase" werte={werte} setzen={setzen}
              auswahl={['L1', 'L2', 'L3', 'L1-L2-L3']} />

        <Feld name="querschnitt" bez="Querschnitt" einheit="mm²"
              zahl werte={werte} setzen={setzen} />

        <Feld name="leiterwerkstoff" bez="Werkstoff" werte={werte} setzen={setzen}
              auswahl={['Cu', 'Al']} />

        <Feld name="belasteteAdern" bez="Belastete Adern"
              werte={werte} setzen={setzen} auswahl={['2', '3', '4']}
              hinweis="Zwei bei Wechselstrom, drei bei Drehstrom. Der Unterschied beträgt etwa 15 % der Belastbarkeit." />

        <Feld name="verlegeart" bez="Verlegeart" werte={werte} setzen={setzen}
              auswahl={['A1', 'A2', 'B1', 'B2', 'C', 'E', 'F']}
              hinweis="B2 im Rohr, C frei auf der Wand oder Pritsche, A2 in wärmegedämmter Wand." />

        <Feld name="haeufung" bez="Häufung" zahl werte={werte} setzen={setzen}
              hinweis="Zahl der gebündelt verlegten Kreise. Bei sieben fällt die Belastbarkeit auf 54 %." />

        <Feld name="umgebungstemperaturC" bez="Umgebungstemperatur" einheit="°C"
              zahl werte={werte} setzen={setzen} />

        <Feld name="laenge" bez="Leitungslänge" einheit="m"
              zahl werte={werte} setzen={setzen}
              hinweis="Ein zu kurzer Wert lässt Spannungsfall UND Abschaltbedingung bestehen." />

        <Feld name="leitungsart" bez="Leitungsart" werte={werte} setzen={setzen} />

        <Feld name="cprKlasse" bez="CPR-Klasse" werte={werte} setzen={setzen} />
      </FormSection>

      <FormSection titel="Schutzeinrichtung" spalten={3}>
        <Feld name="schutzorgan" bez="Schutzorgan" werte={werte} setzen={setzen} />

        <Feld name="kennlinie" bez="Kennlinie" werte={werte} setzen={setzen}
              auswahl={['B', 'C', 'D']}
              hinweis="Sie bestimmt die magnetische Auslösung: B löst beim Fünffachen, C beim Zehnfachen." />

        <Feld name="bemessungsstrom" bez="Bemessungsstrom In" einheit="A"
              zahl werte={werte} setzen={setzen} />

        <Feld name="ausschaltvermoegen" bez="Ausschaltvermögen" einheit="A"
              zahl werte={werte} setzen={setzen}
              hinweis="Muss den maximalen Kurzschlussstrom am Einspeisepunkt beherrschen." />

        <Feld name="schutzorganHersteller" bez="Hersteller"
              werte={werte} setzen={setzen} />

        <Feld name="schutzorganTyp" bez="Typ" werte={werte} setzen={setzen} />
      </FormSection>

      <FormSection
        titel="Fehlerstromschutz und Klemmen"
        spalten={3}
        fuss={(
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <button onClick={speichern} disabled={!geaendert || laeuft}
                    className="m-knopf m-knopf-haupt">
              {laeuft ? 'wird gespeichert …' : 'speichern'}
            </button>

            <button className="m-knopf" onClick={geschlossen}>abbrechen</button>

            <span className="sehr-schwach" style={{ fontSize: 11 }}>
              Nur Geändertes wird geschickt. Ein leeres Feld löscht den Wert.
            </span>
          </div>
        )}
      >
        <Feld name="rcdTyp" bez="RCD-Typ" werte={werte} setzen={setzen}
              auswahl={['A', 'F', 'B', 'B+', 'AC']}
              hinweis="Bei Frequenzumrichtern oder Ladeeinrichtungen ist Typ B nötig — Typ A erkennt dort den Fehlerstrom nicht." />

        <Feld name="rcdDifferenzstrom" bez="I∆n" einheit="mA"
              zahl werte={werte} setzen={setzen} />

        <Feld name="rcdBemessungsstrom" bez="RCD In" einheit="A"
              zahl werte={werte} setzen={setzen} />

        <Feld name="rcdPolzahl" bez="RCD-Pole" werte={werte} setzen={setzen}
              auswahl={['2', '4']} />

        <Feld name="zusatzgeraet" bez="Zusatzgerät" werte={werte} setzen={setzen}
              hinweis="Schütz, AFDD, Motorschutz." />

        <Feld name="klemmeN" bez="Klemme N" werte={werte} setzen={setzen} />
        <Feld name="klemmePe" bez="Klemme PE" werte={werte} setzen={setzen} />
      </FormSection>
    </div>
  );
}
